/**
 * Lead Drop Popup 5018 — KUK-style phone-first lead form.
 *
 * Flow:  phone ONLY  →  (returning lead) OTP screen
 *                    →  (new lead) name / email / qualification / interest
 *                       revealed under the phone, then the OTP screen
 *        →  a brief "OTP verified successfully" card
 *        →  last card (Registered / Welcome back / Download / Login states).
 *
 * Unlike popup 5002 the OTP is a SEPARATE screen with its own segmented boxes,
 * an Edit link back to the phone and a resend countdown — and the extra fields
 * do not exist on screen until the phone lookup says the lead is new.
 *
 * Defaults reproduce Kurukshetra University Online's current form verbatim, so
 * KUK renders unchanged out of the box while any other tenant can restyle it
 * entirely from the admin panel.
 */

/**
 * Real `lead_drop_popup` columns. Anything NOT listed here must be flagged
 * `isJsonField` so Form_4 nests it under `additional_fields` — Sequelize silently
 * drops unknown top-level keys, which would lose the value on save.
 */
const LEAD_DROP_POPUP_COLUMNS = new Set([
  "form_title",
  "form_title_color",
  "form_sub_text",
  "form_sub_text_color",
  "checkbox_description",
  "checkbox_description_color",
  "checkbox_color",
  "terms_conditions_link",
  "privacy_policy_link",
  "new_user_title",
  "new_user_title_color",
  "new_user_description",
  "new_user_description_color",
  "new_user_desktop_image",
  "new_user_image_alt",
  "existing_user_title",
  "existing_user_title_color",
  "existing_user_description",
  "existing_user_description_color",
  "existing_user_desktop_image",
  "existing_user_image_alt",
  "brochure_title",
  "brochure_title_color",
  "brochure_description",
  "brochure_description_color",
  "brochure_link_color",
  "brochure_desktop_image",
  "brochure_image_alt",
  "popup_display_interval_1",
  "popup_display_interval_2",
  "popup_display_interval_3",
  "enable_delay_form",
  "enable_exit_form",
]);

type FieldConfig = {
  fieldName: string;
  label: string;
  value: string | boolean;
  valueType: "string" | "boolean";
  filedType: "INPUT" | "TEXT_AREA" | "COLOR" | "FILE" | "CHECKBOX" | "NUMBER";
  placeholder: string;
  required: boolean;
  defaultValue: string | boolean;
  isJsonField?: boolean;
  rows?: number;
  description?: string;
  validation?: (value: any, formData: Record<string, any>) => string | null;
};

/**
 * Builds one field entry, deriving `isJsonField` from the column list above so the
 * column-vs-JSON decision is made in exactly one place.
 */
const mk = (
  fieldName: string,
  label: string,
  defaultValue: string | boolean,
  filedType: FieldConfig["filedType"],
  extra: Partial<FieldConfig> = {},
): FieldConfig => ({
  fieldName,
  label,
  value: defaultValue,
  valueType: typeof defaultValue === "boolean" ? "boolean" : "string",
  filedType,
  placeholder:
    extra.placeholder ??
    (filedType === "COLOR"
      ? `Enter ${label} (e.g., ${defaultValue || "#FFFFFF"})`
      : filedType === "FILE"
        ? `Upload ${label}`
        : `Enter ${label}`),
  required: false,
  defaultValue,
  ...(LEAD_DROP_POPUP_COLUMNS.has(fieldName) ? {} : { isJsonField: true }),
  ...extra,
});

const text = (n: string, l: string, d: string, e?: Partial<FieldConfig>) =>
  mk(n, l, d, "INPUT", e);
const area = (n: string, l: string, d: string, rows = 2, e?: Partial<FieldConfig>) =>
  mk(n, l, d, "TEXT_AREA", { rows, ...e });
const color = (n: string, l: string, d: string, e?: Partial<FieldConfig>) =>
  mk(n, l, d, "COLOR", e);
const file = (n: string, l: string, d = "", e?: Partial<FieldConfig>) =>
  mk(n, l, d, "FILE", e);
const toggle = (n: string, l: string, d = false, e?: Partial<FieldConfig>) =>
  mk(n, l, d, "CHECKBOX", e);

const LEAD_DROP_POPUP_5018_FIELDS: FieldConfig[] = [
  // ── Header ──────────────────────────────────────────────────────────────────
  text("form_title", "Form Title", "Admissions closing on 29th March."),
  color("form_title_color", "Form Title Color (desktop)", "#9F1C33"),
  text("form_sub_text", "Form Sub Text", "Enroll Now!"),
  color("form_sub_text_color", "Form Sub Text Color (desktop)", "#9F1C33"),
  text("form_helper_text", "Helper Line", "Please enter your details"),
  color("form_helper_text_color", "Helper Line Color (desktop)", "#101112"),

  // ── Card appearance ─────────────────────────────────────────────────────────
  color("form_background_color", "Card Background", "#FFFFFF"),
  // KUK draws a maroon dome behind the heading on mobile only; on desktop the
  // heading sits on the white card beside the image.
  color("heading_band_color", "Mobile Heading Dome Color", "#9F1C33"),
  // Left empty this falls back to KUK's own bundled artwork, so the popup always
  // has its left column. Type "none" to render a single full-width form instead.
  file("side_image", "Side Image (left column on desktop)", "", {
    description:
      "Leave empty for the built-in KUK artwork. Enter \"none\" to hide the image column entirely.",
  }),
  text("side_image_alt", "Side Image Alt Text", ""),
  file("close_icon", "Close Icon (leave empty for the default cross)"),
  text("close_icon_alt", "Close Icon Alt Text", "Close"),

  // ── Field placeholders ──────────────────────────────────────────────────────
  // KUK's form has no visible labels; the placeholder carries the field name.
  text("phone_placeholder", "Phone — Placeholder", "Enter Mobile Number *"),
  text("name_placeholder", "Name — Placeholder", "Enter Full Name *"),
  text("email_placeholder", "Email — Placeholder", "Enter Email *"),
  text(
    "qualification_placeholder",
    "Qualification — Placeholder",
    "Highest Qualification *",
  ),
  text("course_placeholder", "Area Of Interest — Placeholder", "Area Of Interest *"),
  color("error_text_color", "Validation Error Text Color", "#FF0000"),

  // ── Submit button (step 1) ──────────────────────────────────────────────────
  text("button_text", "Submit Button Text", "Apply Now"),
  color("button_text_color", "Submit Button Text Color", "#FFFFFF"),
  color("button_background_color", "Submit Button Background Color", "#9F1C33"),

  // ── OTP screen ──────────────────────────────────────────────────────────────
  // A separate screen in this variant, unlike 5002's in-place OTP field.
  text("otp_title", "OTP — Screen Title", "Enter OTP"),
  text("otp_sent_prefix", "OTP — Sent-To Prefix", "OTP sent to +91", {
    description: 'Rendered as "<prefix><phone number>" above the OTP boxes.',
  }),
  text("otp_edit_text", "OTP — Edit Link Text", "Edit"),
  mk("otp_length", "OTP — Number of Digits", "4", "INPUT", {
    placeholder: "Enter the number of OTP boxes (e.g., 4)",
    description: "How many boxes the OTP is split across. Defaults to 4.",
  }),
  text("otp_button_text", "OTP — Submit Button Text", "Sign Up/Login"),
  text("otp_error_text", "OTP — Error Message", "Incorrect OTP. Please check again."),
  text("resend_prefix_text", "Resend Countdown Prefix", "Resend in", {
    description: 'Rendered as "<prefix> 30 Sec" while the countdown runs.',
  }),
  text("resend_otp_text", "Resend OTP Text", "Resend OTP"),
  mk("resend_countdown_seconds", "Resend Countdown (seconds)", "30", "INPUT", {
    placeholder: "Enter countdown length in seconds (e.g., 30)",
    description: "How long before 'Resend OTP' becomes clickable. Defaults to 30.",
  }),
  color("accent_color", "Accent Color (Edit / Resend links)", "#9F1C33"),

  // ── Verified confirmation ───────────────────────────────────────────────────
  area(
    "verified_title",
    "Verified Screen — Title",
    "OTP verified\nsuccessfully!",
    2,
    {
      description:
        "Held for two seconds after a correct OTP, before the last card. Line breaks are preserved.",
    },
  ),
  color("verified_color", "Verified Screen — Tick & Text Color", "#459F49"),

  // ── Consent ─────────────────────────────────────────────────────────────────
  area(
    "consent_text",
    "Consent Text",
    "By Clicking on sign up I allow Kurukshetra University and its associates to contact me and send program communication via email / SMS / WhatsApp/ voice call etc.",
    3,
  ),
  text("terms_conditions_link", "Terms & Conditions Link", "", {
    placeholder: "https://example.com/terms-and-conditions",
    description:
      "Destination URL for link slot 1. Referenced in Consent Text as {{1:Your link text}}.",
  }),
  text("privacy_policy_link", "Privacy Policy Link", "", {
    placeholder: "https://example.com/privacy-policy",
    description:
      "Destination URL for link slot 2. Referenced in Consent Text as {{2:Your link text}}.",
  }),
  color("consent_text_color", "Consent Text Color", "#4A5568"),

  // ── Last cards — shared ─────────────────────────────────────────────────────
  color("success_circle_color", "Last Card — Decorative Circle Color", "#FDDFE4"),

  // ── Last card — Registered (new user) ───────────────────────────────────────
  text("new_user_title", "New User — Title", "Registered Successfully"),
  color("new_user_title_color", "New User — Title Color", "#101112"),
  area(
    "new_user_description",
    "New User — Description",
    "Thank You for showing interest!\nAn expert from our Career Counselling team will reach out to you",
    3,
  ),
  color("new_user_description_color", "New User — Description Color", "#4A5568"),
  file("new_user_desktop_image", "New User — Image"),
  text("new_user_image_alt", "New User — Image Alt Text", "Registered successfully"),

  // ── Last card — Welcome back (existing user) ────────────────────────────────
  text("existing_user_title", "Existing User — Title", "Welcome back!"),
  color("existing_user_title_color", "Existing User — Title Color", "#101112"),
  area(
    "existing_user_description",
    "Existing User — Description",
    "Our Counsellor Team will call you shortly.",
    2,
  ),
  color(
    "existing_user_description_color",
    "Existing User — Description Color",
    "#4A5568",
  ),
  file("existing_user_desktop_image", "Existing User — Image"),
  text("existing_user_image_alt", "Existing User — Image Alt Text", "Welcome back"),

  // ── Last card — Login states ────────────────────────────────────────────────
  // Used when a CTA opens the popup with the LOGIN variant.
  text("login_new_title", "Login — New User Title", "Welcome"),
  area(
    "login_new_description",
    "Login — New User Description",
    "You have Successfully Registered",
    2,
  ),
  text("login_existing_title", "Login — Returning User Title", "Welcome back!"),
  area(
    "login_existing_description",
    "Login — Returning User Description",
    "Let’s continue your journey.",
    2,
  ),
  color("login_title_color", "Login — Title Color", "#101112"),
  color("login_description_color", "Login — Description Color", "#4A5568"),

  // ── Last card — Download (brochure) ─────────────────────────────────────────
  text("brochure_title", "Download — Title", "Download successful"),
  color("brochure_title_color", "Download — Title Color", "#101112"),
  area(
    "brochure_description",
    "Download — Description (wrap any text in {{ }} to turn it into the manual download link)",
    "Your download will begin automatically. You can {{click here}} to download it manually..",
    3,
  ),
  color("brochure_description_color", "Download — Description Color", "#101112"),
  color(
    "brochure_link_color",
    "Download — Link Color (applied to the {{ }} text)",
    "#2BADF8",
  ),
  file("brochure_desktop_image", "Download — Image"),
  text("brochure_image_alt", "Download — Image Alt Text", "Download successful"),

  // ── Popup display ───────────────────────────────────────────────────────────
  text("popup_display_interval_1", "Popup Display Interval 1", "", {
    placeholder: "Enter Popup Display Interval 1 (e.g., 3000 - for 3 seconds)",
    validation: (value: any, formData: Record<string, any>) => {
      if (
        formData?.enable_delay_form &&
        (!value || value.toString().trim() === "")
      ) {
        return "Popup Display Interval 1 is required when Enable Delay Form is checked";
      }
      return null;
    },
  }),
  text("popup_display_interval_2", "Popup Display Interval 2", "", {
    placeholder: "Enter Popup Display Interval 2 (e.g., 6000 - for 6 seconds)",
  }),
  text("popup_display_interval_3", "Popup Display Interval 3", "", {
    placeholder: "Enter Popup Display Interval 3 (e.g., 12000 - for 12 seconds)",
  }),
  toggle("enable_delay_form", "Enable Delay Form", false, {
    placeholder: "Enable form to show after delay",
  }),
  toggle("enable_exit_form", "Enable Exit Form", false, {
    placeholder: "Enable form to show on exit intent",
  }),
];

/** `DEFAULT_ROW` is derived from the field list so the two can never drift apart. */
const DEFAULT_ROW = LEAD_DROP_POPUP_5018_FIELDS.reduce<Record<string, unknown>>(
  (row, field) => {
    row[field.fieldName] = field.defaultValue;
    return row;
  },
  {},
);

export const LEAD_DROP_POPUP_5018_CONSTANTS = {
  DEFAULT_ROW,
  LEAD_DROP_POPUP_FIELDS_CONFIG: LEAD_DROP_POPUP_5018_FIELDS,
  METADATA_FIELDS: [],
};
